import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { demoLogin } from "@/lib/demo-auth.functions";
import { cn } from "@/lib/utils";

type Props = {
  className?: string;
  variant?: "primary" | "outline" | "steel";
  label?: string;
};

export function DemoLoginButton({ className, variant = "outline", label = "Try Demo — No Signup Needed" }: Props) {
  const navigate = useNavigate();
  const runDemoLogin = useServerFn(demoLogin);
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    setLoading(true);
    try {
      const { access_token, refresh_token } = await runDemoLogin();
      const { error } = await supabase.auth.setSession({ access_token, refresh_token });
      if (error) {
        console.error(error);
        toast.error("Could not start demo. Please try again.");
        setLoading(false);
        return;
      }
      navigate({ to: "/dashboard" });
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Demo unavailable.");
      setLoading(false);
    }
  };

  if (variant === "primary") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={cn(
          "inline-flex items-center justify-center rounded-full px-8 py-3.5 text-base font-semibold text-white transition disabled:opacity-60",
          className,
        )}
        style={{ backgroundImage: "linear-gradient(135deg, #f97316, #dc2626)", boxShadow: "0 0 40px rgba(249,115,22,0.55)" }}
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        {loading ? "Loading demo…" : label}
      </button>
    );
  }

  if (variant === "steel") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={cn(
          "inline-flex w-full items-center justify-center rounded-md px-6 py-3 text-base font-semibold text-white transition hover:opacity-90 disabled:opacity-60",
          className,
        )}
        style={{ backgroundColor: "#f97316", boxShadow: "0 4px 14px rgba(249,115,22,0.35)" }}
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        {loading ? "Loading demo…" : label}
      </button>
    );
  }

  return (
    <Button type="button" variant="outline" className={cn("w-full", className)} onClick={onClick} disabled={loading}>
      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
      {loading ? "Loading demo…" : label}
    </Button>
  );
}
